import React, { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Plus } from 'lucide-react';
import Tooltip from '../../ui/Tooltip';
import { cx } from '../../ui/classNames';
import { historyCommandForKey, isMacPlatform } from '../spreadsheet/keyboardCommands';
import AddSecurityRowEditor from './AddSecurityRowEditor';
import { statusTagClassName } from './addSecurityStyles';

const IS_MAC = isMacPlatform();

const BADGE_TITLES = Object.freeze({
    adding: 'Added to your Watchlist when you save and apply this change.',
    unresolved: "MSI has not resolved this identifier yet. Apply looks it up the same way an upload does; if it can't be matched, it waits for you in Security Resolution.",
    stale: 'This choice is out of date. Remove it and add the security again.',
});

/**
 * The compact status for a pending addition's row: "Adding" in the draft
 * colour, or "Out of date" once Save reports its choice stale. The tooltip
 * describes the badge without replacing its name; the badge cannot take
 * focus, so it keeps that description (a hidden copy outside the row, whose
 * text stays the badge's) for assistive technology when not hovered.
 */
export function AddingBadge({ stale = false, unresolved = false }) {
    const descriptionId = useId();
    let title = BADGE_TITLES.adding;
    if (stale) title = BADGE_TITLES.stale;
    else if (unresolved) title = BADGE_TITLES.unresolved;
    return (
        <Tooltip content={title}>
            <span className={statusTagClassName(stale ? 'danger' : 'warning')} aria-describedby={descriptionId}>
                {stale ? 'Out of date' : 'Adding'}
                {createPortal(<span id={descriptionId} hidden>{title}</span>, document.body)}
            </span>
        </Tooltip>
    );
}

AddingBadge.propTypes = {
    stale: PropTypes.bool,
    unresolved: PropTypes.bool,
};

// The idle row: a borderless button as tall as a grid row (38px), its text in
// the secondary colour at 13px (the surrounding font otherwise), the primary
// colour under a pointer, an inset accent ring for keyboard focus. The
// full-Watchlist note must stay readable, so a disabled row is not dimmed.
const IDLE_BUTTON_CLASS_NAME = cx(
    'm-0 box-border flex h-full min-h-[38px] w-full min-w-0 cursor-pointer appearance-none items-center gap-1.5',
    'rounded border-0 bg-transparent px-1 py-0 text-left [font-family:inherit] [font-style:inherit]',
    '[font-weight:inherit] [line-height:inherit] text-[13px] text-content-secondary',
    '[-webkit-tap-highlight-color:transparent] outline-none focus-visible:outline focus-visible:outline-2',
    'focus-visible:-outline-offset-2 focus-visible:outline-accent disabled:cursor-default',
    '[@media(hover:hover)]:[&:hover:not(:disabled)]:text-content-primary',
);
const TEXT_CLASS_NAME = 'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap';

/**
 * The quiet last row of the Watchlist grid: "+ Add security — type a ticker,
 * ISIN or company". A click, Enter, Space or F2 turns it into the search box
 * (AddSecurityRowEditor); typing a character starts the search with it. Escape
 * brings the row back with focus on it. When the Watchlist is full
 * (`remainingCapacity` 0) it is disabled with a note.
 *
 * `editing` / `onEditingChange(editing, detail)` control the switch when the
 * grid needs to (arrowing past the last row); without `editing` the row keeps
 * its own state. `onNavigateUp` is called for ArrowUp on the idle row, and
 * Mod+Z / Mod+Shift+Z (Ctrl+Y) on it call `onUndo` / `onRedo`. The remaining
 * props go to the editor.
 */
export default function AddSecurityRow({
    editing: editingProp,
    onEditingChange = null,
    onNavigateUp = null,
    disabled = false,
    remainingCapacity = null,
    onPick,
    onExisting = null,
    onCapacityChange = null,
    onUndo = null,
    onRedo = null,
    pendingPicks = undefined,
    searchApi = undefined,
}) {
    const [ownEditing, setOwnEditing] = useState(false);
    // A fresh editor per edit; `restoreFocus` returns focus after Escape.
    const [session, setSession] = useState({ id: 0, initialQuery: '', restoreFocus: false });
    const controlled = editingProp !== undefined;
    const editing = controlled ? editingProp : ownEditing;
    const full = Number.isFinite(remainingCapacity) && remainingCapacity <= 0;
    const idleDisabled = disabled || full;

    const startEditing = (initialQuery = '') => {
        if (idleDisabled) return;
        setSession((current) => ({ id: current.id + 1, initialQuery, restoreFocus: false }));
        if (!controlled) setOwnEditing(true);
        onEditingChange?.(true, { initialQuery });
    };
    const stopEditing = ({ reason }) => {
        setSession((current) => ({ ...current, initialQuery: '', restoreFocus: reason === 'escape' }));
        if (!controlled) setOwnEditing(false);
        onEditingChange?.(false, { reason });
    };

    // An open editor stays open when the Watchlist fills up: it says so itself.
    if (editing) {
        return (
            <AddSecurityRowEditor
                key={session.id}
                initialQuery={session.initialQuery}
                onPick={onPick}
                onCancel={stopEditing}
                onExisting={onExisting}
                onCapacityChange={onCapacityChange}
                onUndo={onUndo}
                onRedo={onRedo}
                pendingPicks={pendingPicks}
                remainingCapacity={remainingCapacity}
                disabled={disabled}
                searchApi={searchApi}
            />
        );
    }

    const handleKeyDown = (event) => {
        const history = historyCommandForKey(event, { isMac: IS_MAC });
        const step = history === 'undo' ? onUndo : onRedo;
        if (history && step) {
            event.preventDefault();
            step();
        } else if (event.key === 'ArrowUp' && onNavigateUp) {
            event.preventDefault();
            onNavigateUp();
        } else if (event.key === 'F2' || event.key === 'Enter') {
            // Enter is handled here, not by the button's own click.
            event.preventDefault();
            startEditing('');
        } else if (
            event.key.length === 1
            && event.key !== ' '
            && !event.ctrlKey
            && !event.metaKey
            && !event.altKey
        ) {
            event.preventDefault();
            startEditing(event.key);
        }
    };

    return (
        <button
            type="button"
            disabled={idleDisabled}
            autoFocus={session.restoreFocus}
            onFocus={() => {
                if (session.restoreFocus) setSession((current) => ({ ...current, restoreFocus: false }));
            }}
            onClick={() => startEditing('')}
            onKeyDown={handleKeyDown}
            className={IDLE_BUTTON_CLASS_NAME}
        >
            <Plus aria-hidden="true" size={18} className="shrink-0" />
            <span className={cx(TEXT_CLASS_NAME, 'shrink-0 font-semibold')}>
                {full ? 'Watchlist full' : 'Add security'}
            </span>
            {/* The space keeps the name readable; a flex container does not render it. */}
            {' '}
            <span className={TEXT_CLASS_NAME}>
                {full ? '— remove a security to add another' : '— type a ticker, ISIN or company'}
            </span>
        </button>
    );
}

AddSecurityRow.propTypes = {
    editing: PropTypes.bool,
    onEditingChange: PropTypes.func,
    onNavigateUp: PropTypes.func,
    disabled: PropTypes.bool,
    remainingCapacity: PropTypes.number,
    onPick: PropTypes.func.isRequired,
    onExisting: PropTypes.func,
    onCapacityChange: PropTypes.func,
    onUndo: PropTypes.func,
    onRedo: PropTypes.func,
    pendingPicks: PropTypes.arrayOf(PropTypes.object),
    searchApi: PropTypes.shape({
        searchWatchlistSecurityDiscovery: PropTypes.func.isRequired,
        previewSecurityResolutionAdd: PropTypes.func.isRequired,
    }),
};
